import type { Tag } from '@dnx/contracts';
import { cn } from '@dnx/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Check, Plus, Tags } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { meQuery } from '../lib/session.js';
import { tagsQuery } from './queries.js';

/** Selos de etiqueta (cor da etiqueta como fundo suave + ponto). */
export function TagChips({ tags, className }: { tags: Tag[]; className?: string }) {
  if (!tags.length) return null;
  return (
    <span className={cn('flex flex-wrap gap-1', className)}>
      {tags.map((t) => (
        <span key={t.id} className="inline-flex h-5 items-center gap-1 rounded-full px-2 text-[11px] font-medium text-fg"
          style={{ backgroundColor: `color-mix(in oklch, ${t.color} 16%, transparent)`, boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${t.color} 35%, transparent)` }}>
          <span className="size-1.5 rounded-full" style={{ backgroundColor: t.color }} aria-hidden />{t.name}
        </span>
      ))}
    </span>
  );
}

/** Etiquetas do contato com edição rápida (marcar/desmarcar). */
export function ContactTags({ contactId, tags, onChanged }: { contactId: string; tags: Tag[]; onChanged?: () => void }) {
  const { data: me } = useSuspenseQuery(meQuery);
  const canApply = me.permissions.includes('contact.tag.apply');
  const { data: all } = useQuery({ ...tagsQuery, enabled: canApply });
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const set = useMutation({
    mutationFn: (tagIds: string[]) => api<Tag[]>(`/contacts/${contactId}/tags`, { method: 'PUT', body: { tagIds } }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['crm'] }); void qc.invalidateQueries({ queryKey: ['inbox'] }); onChanged?.(); },
  });
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const current = new Set(tags.map((t) => t.id));
  const toggle = (id: string) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    set.mutate([...next]);
  };

  return (
    <div ref={box} className="relative flex flex-wrap items-center gap-1">
      <TagChips tags={tags} />
      {canApply && (
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
          className="inline-flex h-5 items-center gap-1 rounded-full border border-dashed border-border-strong px-2 text-[11px] text-fg-muted hover:text-fg">
          {tags.length ? <Tags className="size-3" /> : <Plus className="size-3" />}{tags.length ? 'Etiquetas' : 'Etiqueta'}
        </button>
      )}
      {open && (
        <div className="absolute top-7 left-0 z-30 w-56 rounded-xl border border-border bg-surface p-1.5 shadow-pop" role="menu">
          {!all?.length ? (
            <p className="px-2.5 py-2 text-[12.5px] text-fg-muted">Nenhuma etiqueta criada. A gestão cria em Configurações → Etiquetas.</p>
          ) : all.map((t) => (
            <button key={t.id} type="button" role="menuitemcheckbox" aria-checked={current.has(t.id)} onClick={() => toggle(t.id)}
              className="flex h-9 w-full items-center gap-2.5 rounded-lg px-2.5 text-left text-[13px] text-fg hover:bg-surface-muted">
              <span className="size-2.5 rounded-full" style={{ backgroundColor: t.color }} aria-hidden />
              <span className="flex-1 truncate">{t.name}</span>
              {current.has(t.id) && <Check className="size-4 text-primary" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
